import React, { useState, useEffect } from "react";
import { Package, DollarSign, Eye, Headphones, X } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const Thayumanavar = () => {
  const { language } = useLanguage();
  const [thayumanavarData, setThayumanavarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyPopup, setShowApplyPopup] = useState(false);
  const [showWorksPopup, setShowWorksPopup] = useState(false);

  useEffect(() => {
    fetchThayumanavarData();
  }, []);

  const fetchThayumanavarData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/thayumanavar/data`);
      const result = await response.json();
      
      if (result && result.id) {
        setThayumanavarData(result);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching Thayumanavar data:', error);
      setLoading(false);
    }
  };

  const MEDIA_BASE_URL = 'https://kooturavu.tn.gov.in';

  // Icon mapping for stats
  const iconMap = [
    Package,
    DollarSign,
    Eye,
    Headphones
  ];

  // Helper function to parse about text with bullet points
  const parseAboutText = (text) => {
    if (!text) return { intro: '', bullets: [], conclusion: '' };
    
    const lines = text.split('\n').filter(line => line.trim());
    
    // Find where bullet points start (lines without colons at the end)
    let introEndIndex = 0;
    let bulletsStartIndex = -1;
    let bulletsEndIndex = -1;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // If line ends with colon, it's intro
      if (line.endsWith(':') || line.endsWith('to:')) {
        introEndIndex = i;
        bulletsStartIndex = i + 1;
      }
      // If we're in bullets section and line doesn't look like a bullet
      else if (bulletsStartIndex !== -1 && bulletsEndIndex === -1) {
        // Check if this looks like a conclusion (longer sentence, contains words like "ensures", "scheme")
        if (line.length > 50 || line.toLowerCase().includes('scheme') || 
            line.toLowerCase().includes('ensures') || line.toLowerCase().includes('திட்டம்') ||
            line.toLowerCase().includes('உறுதி')) {
          bulletsEndIndex = i;
          break;
        }
      }
    }
    
    const intro = lines.slice(0, introEndIndex + 1).join(' ');
    const bullets = bulletsStartIndex !== -1 && bulletsEndIndex !== -1 
      ? lines.slice(bulletsStartIndex, bulletsEndIndex) 
      : bulletsStartIndex !== -1 
        ? lines.slice(bulletsStartIndex, -1)
        : [];
    const conclusion = bulletsEndIndex !== -1 ? lines.slice(bulletsEndIndex).join(' ') : lines[lines.length - 1];
    
    return { intro, bullets, conclusion };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!thayumanavarData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">No Thayumanavar data available</div>
      </div>
    );
  }

  const stats = thayumanavarData.stats?.map((stat, index) => ({
    title: language === 'en' ? stat.stats_eng : stat.stats_tamil,
    Icon: iconMap[index % iconMap.length]
  })) || [];

  const aboutContent = parseAboutText(
    language === 'en' ? thayumanavarData.about_eng : thayumanavarData.about_tamil
  );

  const content = {
    en: {
      header: "THAYUMANAVAR SCHEME",
      subheader: "A pioneering initiative delivering essential PDS commodities directly to the homes of senior citizens and persons with disabilities",
      aboutTitle: "About the Scheme",
      keyStats: "Key Stats",
      whoCanApply: "Who can Apply?",
      howWorks: "How the Scheme Works",
      eligibilityClick: "Click to view eligibility criteria",
      processClick: "Click to view the process",
      whoCanApplyPopup: "Who Can Apply?",
      schemeCover: "The scheme covers:",
      eligibilityList: [
        "Senior citizens above 70 years",
        "Persons with 40% or more disability",
        "Bedridden or severely ill cardholders",
        "Individuals living alone or without regular assistance"
      ],
      enrolNote: "Eligible families can enrol through their nearest Fair Price Shop or Civil Supplies office.",
      howWorksPopup: "How the Scheme Works",
      workSteps: [
        "Identification & Enrolment",
        "Verification",
        "Monthly Packing",
        "Doorstep Delivery",
        "Confirmation"
      ]
    },
    ta: {
      header: "தாயுமானவர் திட்டம்",
      subheader: "மூத்த குடிமக்கள் மற்றும் மாற்றுத்திறனாளிகளின் வீடுகளுக்கு நேரடியாக அத்தியாவசிய பொது விநியோக பொருட்களை வழங்கும் முன்னோடி முயற்சி",
      aboutTitle: "திட்டத்தைப் பற்றி",
      keyStats: "முக்கிய புள்ளிவிவரங்கள்",
      whoCanApply: "யார் விண்ணப்பிக்கலாம்?",
      howWorks: "திட்டம் எவ்வாறு செயல்படுகிறது",
      eligibilityClick: "தகுதி விதிமுறைகளைப் பார்க்க கிளிக் செய்யவும்",
      processClick: "செயல்முறையைப் பார்க்க கிளிக் செய்யவும்",
      whoCanApplyPopup: "யார் விண்ணப்பிக்கலாம்?",
      schemeCover: "இந்தத் திட்டம் உள்ளடக்கியது:",
      eligibilityList: [
        "70 வயதுக்கு மேற்பட்ட மூத்த குடிமக்கள்",
        "40% அல்லது அதற்கு மேற்பட்ட ஊனமுற்ற நபர்கள்",
        "படுக்கையில் உள்ள அல்லது கடுமையாக நோய்வாய்ப்பட்ட அட்டைதாரர்கள்",
        "தனியாக வாழும் அல்லது வழக்கமான உதவி இல்லாத நபர்கள்"
      ],
      enrolNote: "தகுதியுள்ள குடும்பங்கள் அருகிலுள்ள நியாய விலைக் கடை அல்லது சிவில் சப்ளைஸ் அலுவலகம் மூலம் பதிவு செய்யலாம்.",
      howWorksPopup: "திட்டம் எவ்வாறு செயல்படுகிறது",
      workSteps: [
        "அடையாளம் மற்றும் பதிவு",
        "சரிபார்ப்பு",
        "மாதாந்திர பேக்கிங்",
        "வீட்டு வாசலில் டெலிவரி",
        "உறுதிப்படுத்தல்"
      ]
    }
  };

  const t = content[language];

  return (
    <div className="bg-slate-50 font-sans py-12 px-6 md:px-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
          {t.header}
        </h1>
        <p className="mt-2 text-gray-500 md:text-lg">
          {t.subheader}
        </p>
      </div>

      {/* About the Scheme - Full Width */}
      <div className="bg-blue-50 rounded-xl p-8 mb-12">
        <div className="w-full">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            {t.aboutTitle}
          </h2>

          <div className="border-l-4 border-blue-500 pl-6">
            {/* Intro text */}
            <p className="text-gray-700 mb-4 leading-relaxed">
              {aboutContent.intro}
            </p>

            {/* Bullet points */}
            {aboutContent.bullets.length > 0 && (
              <ul className="list-disc list-inside text-gray-700 space-y-2 font-medium mb-4">
                {aboutContent.bullets.map((bullet, index) => (
                  <li key={index}>{bullet}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Conclusion text - full width */}
          <p className="text-gray-700 mt-6">
            {aboutContent.conclusion}
          </p>
        </div>
      </div>

  
      {/* Who can Apply + How the Scheme Works */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Who can Apply */}
        <div
          onClick={() => setShowApplyPopup(true)}
          className="rounded-2xl cursor-pointer transition-all duration-300 shadow-sm hover:shadow-md"
          style={{
            backgroundColor: "#fcf5ff",  
            border: "10px solid #eedbfd",
          }}
        >
          <div className="p-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-white shadow-sm rounded-xl flex items-center justify-center">
                <Headphones className="w-7 h-7 text-[#6e261e]" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-[#1a1a1a] text-center mb-3">
              {t.whoCanApply}
            </h2>

            <p className="text-gray-700 text-center text-sm">
              {t.eligibilityClick}
            </p>
          </div>
        </div>

        {/* How the Scheme Works */}
        <div
          onClick={() => setShowWorksPopup(true)}
          className="rounded-2xl cursor-pointer transition-all duration-300 shadow-sm hover:shadow-md"
          style={{
            backgroundColor: "#fff8ef",
            border: "10px solid #ffe4c4",
          }}
        >
          <div className="p-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-white shadow-sm rounded-xl flex items-center justify-center">
                <Package className="w-7 h-7 text-[#5f729b]" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-[#1a1a1a] text-center mb-3">
              {t.howWorks}
            </h2>

            <p className="text-gray-700 text-center text-sm">
              {t.processClick}
            </p>
          </div>
        </div>

      </div>

      {/* Who can Apply Popup */}
      {showApplyPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-8 relative shadow-2xl">
            <button
              onClick={() => setShowApplyPopup(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              {t.whoCanApplyPopup}
            </h2>

            <div className="bg-blue-50 rounded-xl p-6 shadow-inner">
              <p className="font-semibold text-gray-800 mb-4">{t.schemeCover}</p>

              <div className="space-y-6">
                {t.eligibilityList.map((item, idx) => (
                  <div key={idx} className="flex items-start relative pl-12">
                    {idx < t.eligibilityList.length - 1 && (
                      <div className="absolute left-5 top-8 w-0.5 h-full bg-blue-300"></div>
                    )}

                    <div className="absolute left-0 flex items-center justify-center 
                        w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-semibold shadow">
                      {idx + 1}
                    </div>

                    <p className="text-gray-700 font-medium leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-gray-600 text-sm mt-5 text-center">
              {t.enrolNote}
            </p>
          </div>
        </div>
      )}

      {/* How the Scheme Works Popup */}
      {showWorksPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full p-8 relative shadow-2xl">
            <button
              onClick={() => setShowWorksPopup(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">{t.howWorksPopup}</h2>

            <div className="space-y-6">
              {t.workSteps.map((step, idx) => (
                <div key={idx} className="flex items-start relative pl-12">
                  
                  {idx < t.workSteps.length - 1 && (
                    <div className="absolute left-5 top-10 w-0.5 h-full bg-blue-300"></div>
                  )}

                  <div className="absolute left-0 flex items-center justify-center w-10 h-10 rounded-full bg-blue-500 text-white font-semibold">
                    {idx + 1}
                  </div>

                  <p className="text-gray-700 font-medium leading-relaxed">{step}</p>
                </div>
              ))}
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default Thayumanavar;